import urlResolver from 'admin/utils/URLResolver';
import { useEffect } from 'react';
import useSWRInfinite from 'swr/infinite';
import { CourseFeature } from 'edrolo/domains/coursePortal/types/Courses';

// The accessible-courses endpoint paginates results (max 100 per page). We request the
// maximum page size and follow `next` links until exhausted, so membership checks (e.g.
// FeatureGate's per-course mode) see every accessible course regardless of count.
const MAX_LIMIT = 100;

export interface AccessibleCourse {
  id: number;
  title: string;
}

interface PaginatedAccessibleCourses {
  count: number;
  next: string | null;
  previous: string | null;
  results: AccessibleCourse[];
}

export interface IUseAccessibleCourses {
  data: AccessibleCourse[];
  loading: boolean;
  error: Error | undefined | unknown;
}

// Lists the courses the user can access (via school subscription or class membership),
// optionally filtered to those whose resolved feature-set contains all of the given
// features (e.g. features=['TASK_SETTING']).
export const useAccessibleCourses = (
  features?: CourseFeature[]
): IUseAccessibleCourses => {
  const firstPageUrl = urlResolver.query('api:accessible-courses', null, {
    feature: features?.length ? features.join(',') : undefined,
    limit: MAX_LIMIT,
  });

  const { data, error, size, setSize, isValidating } =
    useSWRInfinite<PaginatedAccessibleCourses>(
      (index, previousPageData) => {
        if (index === 0) {
          return firstPageUrl;
        }
        // Stop once the previous page has no `next` link.
        return previousPageData?.next ?? null;
      },
      { revalidateFirstPage: false }
    );

  // Keep advancing while the latest fetched page still has a `next` link, so the
  // aggregated list is complete before gating checks run against it.
  const lastPage = data?.[data.length - 1];
  const hasMore = Boolean(lastPage?.next);

  useEffect(() => {
    if (hasMore && !isValidating && !error) {
      setSize(size + 1);
    }
  }, [hasMore, isValidating, size, setSize, error]);

  if (error) {
    console.error(error);
  }

  const courses = data?.flatMap((page) => page.results) ?? [];

  return {
    data: courses,
    error,
    // Treat pending pages as still loading to avoid a premature false negative.
    // Clear loading immediately on error so consumers can show the error fallback.
    loading: !error && (!data || hasMore),
  };
};
